import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Award, Users, MapPin, Calendar, Plane, Shield, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';
import { cn } from '../lib/utils';

const stats = [
  { icon: Calendar, value: 15, suffix: '+', label: 'Years Experience' },
  { icon: Users, value: 25000, suffix: '+', label: 'Happy Travelers' },
  { icon: MapPin, value: 100, suffix: '+', label: 'Destinations' },
  { icon: Plane, value: 500, suffix: '+', label: 'Tour Packages' },
];

const values = [
  {
    icon: Heart,
    title: 'Passion for Travel',
    description: 'We live and breathe travel. Every journey we craft is infused with our love for exploration and discovery.'
  },
  {
    icon: Shield,
    title: 'Trust & Safety',
    description: 'Your safety is our priority. We partner only with verified vendors and maintain the highest safety standards.'
  },
  {
    icon: Star,
    title: 'Excellence',
    description: 'We strive for excellence in everything we do, from planning to execution, ensuring memorable experiences.'
  },
  {
    icon: Users,
    title: 'Customer First',
    description: 'Your satisfaction drives us. We go above and beyond to exceed expectations and create lasting memories.'
  },
];

const team = [
  {
    name: 'Rajesh Kumar',
    role: 'Founder & CEO',
    image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400',
    quote: 'Travel is the only thing you buy that makes you richer.'
  },
  {
    name: 'Priya Sharma',
    role: 'Head of Operations',
    image: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=400',
    quote: 'Every trip should be a story worth telling.'
  },
  {
    name: 'Amit Patel',
    role: 'Travel Consultant',
    image: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400',
    quote: 'The world is a book, and those who do not travel read only a page.'
  },
  {
    name: 'Sneha Reddy',
    role: 'Customer Relations',
    image: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400',
    quote: 'Making dreams come true, one trip at a time.'
  },
];

function AnimatedCounter({ value, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const end = value;
          const duration = 2000;
          const increment = end / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, hasAnimated]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  );
}

export default function AboutPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#004E64] to-[#006B87] py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FF6B35] rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
              ✨ About NYRAV Tours
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Crafting Unforgettable<br />
              <span className="text-[#FF6B35]">Travel Experiences</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Since 2009, we&apos;ve been turning travel dreams into reality. Our passion for exploration 
              and commitment to excellence has made us one of India&apos;s most trusted travel partners.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative -mt-16 z-10 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-7 h-7 text-[#FF6B35]" />
                  </div>
                  <div 
                    className="text-3xl md:text-4xl font-bold text-[#004E64] mb-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-[#4A4A4A] text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
                Our Story
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                A Journey That Began With a Dream
              </h2>
              <div className="space-y-4 text-[#4A4A4A] text-lg leading-relaxed">
                <p>
                  NYRAV Tours and Travels was born out of a simple yet powerful idea: everyone deserves 
                  to experience the magic of travel. Founded in 2009 by a group of passionate travelers, 
                  we started with just a handful of domestic tour packages.
                </p>
                <p>
                  Over the years, we&apos;ve grown from a small local agency to a renowned travel company 
                  serving thousands of happy customers across India and beyond. Our growth is a testament 
                  to our unwavering commitment to quality, transparency, and customer satisfaction.
                </p>
                <p>
                  Today, with over 500 curated tour packages spanning 100+ destinations worldwide, 
                  we continue to innovate and evolve, always keeping the traveler&apos;s experience at 
                  the heart of everything we do.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Travel destination"
                  className="rounded-2xl h-64 object-cover"
                />
                <img 
                  src="https://images.pexels.com/photos/1591373/pexels-photo-1591373.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Happy travelers"
                  className="rounded-2xl h-64 object-cover mt-8"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#FF6B35] text-white rounded-2xl p-6 shadow-xl">
                <p className="text-3xl font-bold">15+</p>
                <p className="text-sm">Years of Trust</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
              What Drives Us
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Our Mission & Vision
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#004E64] rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 
                className="text-2xl font-bold text-[#1A1A1A] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Our Mission
              </h3>
              <p className="text-[#4A4A4A] leading-relaxed">
                To make travel accessible, enjoyable, and meaningful for everyone. We strive to create 
                personalized experiences that connect people with cultures, nature, and themselves, 
                while maintaining the highest standards of service and safety.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 
                className="text-2xl font-bold text-[#1A1A1A] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Our Vision
              </h3>
              <p className="text-[#4A4A4A] leading-relaxed">
                To become India&apos;s most loved and trusted travel company, known for transforming 
                ordinary trips into extraordinary journeys. We envision a world where travel breaks 
                barriers and creates lasting bonds between people and places.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
              Our Core Values
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="group bg-gray-50 hover:bg-[#004E64] rounded-2xl p-6 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#FF6B35]/10 group-hover:bg-white/20 rounded-2xl flex items-center justify-center mb-4 transition-colors">
                  <value.icon className="w-7 h-7 text-[#FF6B35] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] group-hover:text-white mb-2 transition-colors">
                  {value.title}
                </h3>
                <p className="text-[#4A4A4A] group-hover:text-white/80 text-sm transition-colors">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
              Meet The Team
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              The People Behind Your Journeys
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              Our dedicated team of travel enthusiasts works tirelessly to ensure your trips are nothing short of perfect.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all group"
              >
                <div className="h-64 overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {member.name}
                  </h3>
                  <p className="text-[#FF6B35] text-sm mb-3">{member.role}</p>
                  <p className="text-[#4A4A4A] text-sm italic">&ldquo;{member.quote}&rdquo;</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF6B35] to-[#E85A2D]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Let us help you plan the perfect trip. Our travel experts are ready to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tours">
              <Button size="lg" className="bg-white text-[#FF6B35] hover:bg-gray-100 rounded-full px-8">
                Explore Tours <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF6B35] rounded-full px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
